/**
 * AI configuration for the org's business (moved from Business → Misc,
 * #6232 reshuffle): Additional AI Instructions + how the business is
 * described (products vs services, brand adherence). Self-contained: own
 * fetch, own dirty state, own Save.
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Spinner } from '@/components/glitch-ui/spinner';
import { useToast } from '@/hooks/useToast';
import { updateBusiness } from '@/lib/apis';
import { businessQueryOptions } from '@/lib/query-options';
import type { BusinessSettings } from '@/lib/types';

// #5546: shown as the field placeholder; the saved value stays empty until
// the user types (the backend applies this text when the saved value is
// empty). KEEP IN SYNC with backend/app/pipeline/headlines.py.
const DEFAULT_AI_INSTRUCTIONS =
  'Write clear, benefit-led ad copy in a professional, trustworthy tone. Lead with the core value ' +
  'proposition and a concrete customer outcome. Use plain language: avoid jargon, hype, and ' +
  'unverifiable claims. Prefer specifics over vague adjectives. Include a natural call to action, ' +
  'keep it concise and skimmable, and never name or compare against competitors.';

function Toggle({ on, onToggle }: { on: boolean; onToggle: () => void }) {
  return (
    <div
      onClick={onToggle}
      className='relative w-12 h-6 rounded-full transition-colors duration-200 cursor-pointer bg-[#290BD9]'
    >
      <div
        className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-200 ${
          on ? 'right-0.5' : 'left-0.5'
        }`}
      />
    </div>
  );
}

export function BusinessAiConfiguration({ orgId }: { orgId: number }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: business } = useQuery({ ...businessQueryOptions(orgId), enabled: orgId > 0 });
  const settings: BusinessSettings = useMemo(
    () => business?.settings || { sells_services: false, brand_adherence_mode: 'strict', llm_instructions: null },
    [business],
  );

  const [llmInstructions, setLlmInstructions] = useState('');
  const [sellsServices, setSellsServices] = useState(false);
  const [discoverMode, setDiscoverMode] = useState(false);

  useEffect(() => {
    setLlmInstructions(settings.llm_instructions === DEFAULT_AI_INSTRUCTIONS ? '' : settings.llm_instructions || '');
    setSellsServices(settings.sells_services ?? false);
    setDiscoverMode(settings.brand_adherence_mode === 'discovery');
  }, [settings]);

  const isDirty =
    llmInstructions !== (settings.llm_instructions === DEFAULT_AI_INSTRUCTIONS ? '' : settings.llm_instructions || '') ||
    sellsServices !== (settings.sells_services ?? false) ||
    discoverMode !== (settings.brand_adherence_mode === 'discovery');

  const saveMutation = useMutation({
    mutationFn: () =>
      updateBusiness(business?.id ?? 0, {
        settings: {
          ...settings,
          sells_services: sellsServices,
          brand_adherence_mode: discoverMode ? 'discovery' : 'strict',
          llm_instructions: llmInstructions,
        },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['business'] }),
    onError: (err: Error) => toast({ description: err.message || 'The configuration could not be saved.', variant: 'destructive' }),
  });

  if (!business) return null;

  return (
    <div className='space-y-6'>
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
        <div>
          <h3 className='text-lg font-semibold text-gray-900 mb-1'>Additional instructions</h3>
          <p className='text-sm text-gray-700 mb-4'>Any additional context for ad generation.</p>
          <Textarea
            className='bg-white min-h-[200px]'
            maxLength={400}
            placeholder={DEFAULT_AI_INSTRUCTIONS}
            value={llmInstructions}
            onChange={(e) => setLlmInstructions(e.target.value)}
          />
          <p className='text-xs text-gray-400 mt-1 text-right'>{llmInstructions.length}/400 characters</p>
        </div>
        <div>
          <h3 className='text-lg font-semibold text-gray-900 mb-1'>Configuration</h3>
          <p className='text-sm text-gray-700 mb-4'>Configure how your business is described</p>
          <div className='space-y-4'>
            <div className='flex items-center justify-between'>
              <span className='text-base font-medium text-gray-700'>What you sell</span>
              <div className='flex items-center space-x-3'>
                <span className='text-sm text-gray-500 w-16 text-right'>Products</span>
                <Toggle on={sellsServices} onToggle={() => setSellsServices((v) => !v)} />
                <span className='text-sm text-gray-500 w-16'>Services</span>
              </div>
            </div>
            <div>
              <div className='flex items-center justify-between'>
                <span className='text-base font-medium text-gray-700'>Brand adherence</span>
                <div className='flex items-center space-x-3'>
                  <span className='text-sm text-gray-500 w-16 text-right'>Strict</span>
                  <Toggle on={discoverMode} onToggle={() => setDiscoverMode((v) => !v)} />
                  <span className='text-sm text-gray-500 w-16'>Discovery</span>
                </div>
              </div>
              <p className='text-sm text-gray-500 mt-2'>
                In strict mode, Glitch always generates content consistent with your brand values. In discovery mode,
                it tests a broader range of permutations to find higher performing ads but may not always align
                strictly to brand.
              </p>
            </div>
          </div>
        </div>
      </div>
      {isDirty && (
        <div className='flex justify-end'>
          <Button
            className='h-9 bg-[#290BD9] hover:bg-[#2309C5] text-white'
            disabled={saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            {saveMutation.isPending && <Spinner size='sm' className='mr-2' />}
            Save Changes
          </Button>
        </div>
      )}
    </div>
  );
}
